import { Controller } from "stimulus";
import * as Sentry from "@sentry/browser";

export default class extends Controller {
  static targets = [
    "errorPanel",
    "buttonHint",
    "spinner",
    "formWrapper",
    "paymentMethodTabs",
  ];

  connect() {
    this.initForm();
    if (this.data.get("chargeableSourceStatus") === "FAILED") {
      this.error = this.data.get("chargeableSourceErrorMessage");
    } else {
      this.updateErrorView();
    }
  }

  disconnect() {
    this.card.removeEventListener(this.handleCardError.bind(this));
  }

  get stripePaymentIntentSecret() {
    return this.data.get("stripe-payment-intent-secret");
  }

  get stripePublishableKey() {
    return this.data.get("stripe-publishable-key");
  }

  get paymentAmount() {
    return this.data.get("payment-amount");
  }

  get brandColour() {
    return this.data.get("brand-colour");
  }

  get purchaserFirstName() {
    return this.data.get("purchaserFirstName");
  }

  get purchaserLastName() {
    return this.data.get("purchaserLastName");
  }

  get error() {
    return this.data.get("error");
  }

  get amountInCents() {
    return parseInt(this.paymentAmount * 100);
  }

  set error(error) {
    this.data.set("error", error);
    this.publishPurchaseErrorUpdatedEvent();
    this.updateErrorView();
  }

  initForm() {
    this.stripe = Stripe(this.stripePublishableKey);
    let elements = this.stripe.elements({ locale: this.data.get("locale") });
    let style = {
      base: {
        fontSize: "16px",
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        color: "#505E75",
        "::placeholder": {
          fontStyle: "italic",
        },
      },
      invalid: {
        iconColor: "#E74C3C",
        color: "#E74C3C",
      },
    };
    this.card = elements.create("card", { style: style, hidePostalCode: true });
    this.card.on("ready", this.stripeLoaded.bind(this));
    this.card.addEventListener("change", this.handleCardError.bind(this));
    this.card.mount("#stripe-payment-form");
  }

  handleCardError({ error }) {
    if (error) {
      this.error = error.message;
    } else {
      this.error = "";
    }
  }

  stripeLoaded() {
    this.card.focus();
    this.hideSpinner();
  }

  publishPaymentConfirmedEvent() {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent("payment-confirmed", true, true, null);
    this.element.dispatchEvent(event);
  }

  publishPurchaseErrorUpdatedEvent() {
    const event = document.createEvent("CustomEvent");
    event.initCustomEvent("purchase-error-updated", true, true, null);
    event.purchaseError = this.error;
    this.element.dispatchEvent(event);
  }

  updateErrorView() {
    this.errorPanelTarget.innerHTML = this.error;
    this.errorPanelTarget.classList.toggle("hidden", this.error === "");
  }

  async confirmPayment(event) {
    event.target.disabled = true;
    this.error = "";
    this.showSpinner();
    event.preventDefault();

    try {
      await this.verifySessionExists();
      let self = this;
      this.stripe
        .confirmCardPayment(this.stripePaymentIntentSecret, {
          payment_method: {
            card: this.card,
            billing_details: {
              name: `${this.purchaserFirstName} ${this.purchaserLastName}`,
            },
          },
        })
        .then(function (result) {
          if (result.error) {
            event.target.disabled = false;
            self.hideSpinner();
            self.error = result.error.message;
          } else {
            if (result.paymentIntent.status === "succeeded") {
              self.publishPaymentConfirmedEvent();
            }
          }
        });
    } catch (e) {
      Sentry.captureException(e);
      this.refreshPage();
    }
  }

  async initiateGiropay() {
    // Create a GP source in JS
    try {
      await this.verifySessionExists();
      this.stripe
        .createSource({
          type: "giropay",
          amount: this.amountInCents,
          currency: this.data.get("currencyCode"),
          statement_descriptor: this.data.get("salonName"),
          metadata: {
            client_app: "ONLINE_VOUCHERS_SERVICE",
            environment: this.data.get("environment"),
            purchase_number: this.data.get("purchaseNumber"),
            business_uri: this.data.get("businessUri"),
            branch_uri: this.data.get("branchUri"),
          },
          owner: {
            name: `${this.data.get("purchaserFirstName")} ${this.data.get(
              "purchaserLastName"
            )}`,
          },
          redirect: {
            return_url: `${window.location.protocol}//${window.location.host}${
              window.location.pathname
            }/stripe/giropay/${this.data.get("purchaseNumber")}`,
          },
        })
        .then(function (result) {
          console.log(result);
          // handle result.error or result.source
          if (result.error) {
            // show error
          } else {
            // Redirect to URL given in source result
            window.location.href = result.source.redirect.url;
          }
        });
    } catch (e) {
      Sentry.captureException(e);
      this.refreshPage();
    }
  }

  showSpinner() {
    $(this.buttonHintTarget).addClass("invisible");
    $(this.buttonHintTarget).removeClass("opacity-100");
    $(this.formWrapperTarget).addClass("invisible");
    $(this.formWrapperTarget).removeClass("opacity-100");
    $(this.paymentMethodTabsTarget).addClass("invisible");
    $(this.paymentMethodTabsTarget).removeClass("opacity-100");
    $(this.spinnerTarget).fadeIn(100);
  }

  hideSpinner() {
    let self = this;
    $(this.spinnerTarget).fadeOut(100, () => {
      $(self.buttonHintTarget).removeClass("invisible");
      $(self.buttonHintTarget).addClass("opacity-100");
      $(self.formWrapperTarget).removeClass("invisible");
      $(self.formWrapperTarget).addClass("opacity-100");
      $(self.paymentMethodTabsTarget).removeClass("invisible");
      $(self.paymentMethodTabsTarget).addClass("opacity-100");
    });
  }

  async verifySessionExists() {
    let response = await fetch(
      `${window.location.pathname}/sessions/${this.data.get("purchaseNumber")}`
    );
    if (response.status != 200) {
      let body = await response.json();
      throw Error(body.error);
    }
  }

  refreshPage() {
    window.location = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  }
}
